import { Component, DoCheck, Input, OnInit } from '@angular/core';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { ScheduledEvent, WaitlistEntry, WaitlistEntryStatus } from '../../../../../../../../../../../models/scheduled-event';
import {
  CallbackResult,
  CallbackResultStatus,
  dateStringFromISO8601,
  getISO8601DateString,
  timeStringFromISO8601
} from '../../../../../../../../../../../utils';
import { ScheduledEventService } from '../../../../../../../../../../../services/scheduled-event/scheduled-event.service';
import { Event } from '../../../../../../../../../../../models/event';
import { EventService } from '../../../../../../../../../../../services/event/event.service';
import { eventFullDueToSecondaryCapacity } from '../../../../../../../../../../../utils-models';
import { isGuardianWithEmail } from '../../../../../../../../../../../models/customer';
import { CustomerService } from '../../../../../../../../../../../services/customer/customer.service';
import { EventTypeService } from '../../../../../../../../../../../services/event-type/event-type.service';
import { EventType } from '../../../../../../../../../../../models/event-type';
import { AgeRestrictionBypass, eventAgeAsOfDate, waitlistEntryAgeBirthday } from '../../../../../../../../../../../models/age-restriction';


@Component({
  selector: 'app-invite-waitlist-entry-to-book-modal',
  templateUrl: './invite-waitlist-entry-to-book-modal.component.html',
  styleUrls: ['./invite-waitlist-entry-to-book-modal.component.css']
})
export class InviteWaitlistEntryToBookModalComponent implements OnInit, DoCheck {

  @Input() waitlistEntry: WaitlistEntry;
  @Input() waitlistScheduledEvent: ScheduledEvent | null = null;
  @Input() inviteSentCallback: (result: CallbackResult) => undefined;

  public loadingEventsForDate = false;
  public loadingSelectedEvent = false;
  public loadingSubmit = false;

  // Only used if this is an event type-wide waitlist (scheduledEvent === null).
  public selectedDate: string;
  public scheduledEventsForDate: ScheduledEvent[] = [];
  public selectedEvent: Event = null;

  // Only used if this is an event's waitlist, for the event's start date.
  public waitlistEvent: Event = null;
  public loadingWaitlistEvent = false;

  public eventType: EventType = null;
  public loadingEventType = false;

  public readonly ageRestriction = new AgeRestrictionBypass();

  // The optional booking deadline. Left blank, a re-send keeps the entry's
  // current deadline and a first send uses the event type's acceptance window.
  public newDeadlineDate = '';
  public newDeadlineTime = '';

  // The note that will be included in the invitation email. Prefilled from the
  // entry so a re-send repeats the note the participant was originally sent;
  // staff can edit or empty it.
  public inviteNote = '';

  // Whether the invitation should also go to the participant's linked parents
  // and guardians. Asked on each send rather than remembered: the entry has no
  // record of what the last invitation chose.
  public sendInviteToParentsAndGuardians = false;

  // Whether any linked parent or guardian has an email address. Null while
  // unknown -- not looked up (the entry already has a recipient, so it does not
  // matter) or the lookup failed.
  public guardianWithEmailExists: boolean | null = null;
  public loadingRelations = false;

  private relationsRequestCount = 0;

  public errorMessage = '';
  public serverError: number = null;

  public readonly WaitlistEntryStatus = WaitlistEntryStatus;
  public readonly timeStringFromISO8601 = timeStringFromISO8601;
  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly eventFullDueToSecondaryCapacity = eventFullDueToSecondaryCapacity;

  // The number of loadEventsForDate calls. Used to ensure that only the most
  // recent result is displayed, in the event of concurrent requests (due to,
  // e.g., rapid pagination).
  private loadEventsForDateRequestCount = 0;

  constructor(
    public activeModal: NgbActiveModal,
    private scheduledEventService: ScheduledEventService,
    private eventService: EventService,
    private customerService: CustomerService,
    private eventTypeService: EventTypeService,
  ) {}

  ngOnInit() {
    this.inviteNote = this.waitlistEntry.invite_note || '';

    this.loadGuardianEmailAvailability();
    this.loadEventType();

    if (this.waitlistScheduledEvent !== null) {
      this.loadWaitlistEvent();
    }

    if (this.waitlistScheduledEvent === null) {
      this.selectedDate = getISO8601DateString(new Date());

      // A re-send preselects the event the participant was already invited
      // to (PRO-1489).
      if (this.isResend() && this.waitlistEntry.event) {
        this.loadSelectedEvent(this.waitlistEntry.event.id);
      }

      this.loadEventsForDate();
    }
  }

  ngDoCheck() {
    const event = (this.waitlistScheduledEvent === null) ? this.selectedEvent : this.waitlistEvent;

    if ((event === null) || (this.eventType === null)) {
      this.ageRestriction.refresh('', [], null);
      return;
    }

    this.ageRestriction.refresh(
      'event-' + event.id.toString(),
      [{
        key: this.waitlistEntry.id,
        name: this.waitlistEntry.first_name + ' ' + this.waitlistEntry.last_name,
        birthday: waitlistEntryAgeBirthday(this.waitlistEntry),
      }],
      this.eventType,
      eventAgeAsOfDate(event)
    );
  }

  public loadEventsForDate() {
    this.scheduledEventsForDate = [];

    if (!this.selectedDate) { return; }

    this.loadingEventsForDate = true;

    // Increment the request count and save a local copy.
    this.loadEventsForDateRequestCount++;
    const thisRequestCount = this.loadEventsForDateRequestCount;

    this.scheduledEventService
      .getScheduledEvents(
        this.selectedDate,
        this.waitlistEntry.event_type_id,
        false
      )
      .then(scheduledEvents => {
        // Only update the events list if there has not been a more recent
        // request initiated.
        if (thisRequestCount !== this.loadEventsForDateRequestCount) {
          return;
        }

        this.scheduledEventsForDate = scheduledEvents;
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => {
        if (thisRequestCount === this.loadEventsForDateRequestCount) {
          this.loadingEventsForDate = false;
        }
      });
  }

  public loadSelectedEvent(selectedEventId: number) {
    this.loadingSelectedEvent = true;

    this.eventService
      .getEvent(selectedEventId)
      .then(event => {
        this.selectedEvent = event;
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingSelectedEvent = false;
      });
  }

  /**
   * Whether this is a re-send of an invitation the participant already has,
   * rather than a first invitation off the waitlist.
   */
  public isResend(): boolean {
    return (this.waitlistEntry.status === WaitlistEntryStatus.pending);
  }

  /**
   * The addresses this invitation will reach, in the order the email helper
   * assembles them (mirrors `invite_waitlist_entry_to_book`). Guardians are
   * not listed: they are a per-send choice and their addresses are not on
   * the entry.
   */
  public emailRecipients(): string[] {
    if (this.participantIsRemoved()) { return []; }

    const entryAddresses = [
      this.waitlistEntry.notification_email,
      this.waitlistEntry.booking_customer_email
    ].filter(
      (email, index, all) => (!!email && (all.indexOf(email) === index))
    );

    // The participant's own address is a fallback, not an addition: the
    // endpoint only asks for it when the entry has no address of its own. See
    // invite_waitlist_entry_to_book.
    if (entryAddresses.length > 0) { return entryAddresses; }

    return this.waitlistEntry.participant_customer_email
      ? [this.waitlistEntry.participant_customer_email]
      : [];
  }

  /**
   * Whether this invitation has no address to go to. With no recipient the
   * email helper returns without sending, so the invite succeeds silently;
   * staff are warned rather than blocked.
   */
  public hasNoEmailRecipient(): boolean {
    // Before the guardians clause: a removed participant stops the helper
    // before it would add a guardian's address either.
    if (this.participantIsRemoved()) { return true; }

    if (this.emailRecipients().length > 0) { return false; }

    // Only claim nothing will be sent when we know no guardian can receive it.
    // A failed or pending lookup leaves this null, and an unproven warning is
    // worse than none.
    if (this.sendInviteToParentsAndGuardians &&
        (this.guardianWithEmailExists !== false)) {
      return false;
    }

    return true;
  }

  /**
   * Whether the participant has been removed. The invitation email helpers
   * return at their `participant_customer.is_active` guard before assembling
   * any recipient list, guardians included.
   */
  public participantIsRemoved(): boolean {
    return (this.waitlistEntry.participant_customer_is_active === false);
  }

  /** The zone the endpoint reads the deadline in: that of the event the entry is invited to. */
  public deadlineTimeZone(): string {
    if (this.waitlistScheduledEvent === null && this.selectedEvent !== null) {
      return this.selectedEvent.time_zone;
    }
    return this.waitlistEntry.time_zone;
  }

  public handleClearNewDeadline() {
    this.newDeadlineDate = '';
    this.newDeadlineTime = '';
  }

  /**
   * Whether a new deadline has been half-filled in. Both halves are needed,
   * and neither is required, so this is the one combination to block.
   */
  public newDeadlineIncomplete(): boolean {
    return (
      (!!this.newDeadlineDate && !this.newDeadlineTime) ||
      (!this.newDeadlineDate && !!this.newDeadlineTime)
    );
  }

  public submitDisabled(): boolean {
    return (
      ((this.waitlistScheduledEvent === null) && (this.selectedEvent === null)) ||
      this.newDeadlineIncomplete() ||
      this.loadingEventType ||
      this.loadingWaitlistEvent ||
      this.ageRestriction.blocksSend()
    );
  }

  public handleSendInvite() {
    this.loadingSubmit = true;
    this.errorMessage = '';

    let invitePromise: Promise<void>;
    if (this.waitlistScheduledEvent === null) {
      invitePromise = this.scheduledEventService.inviteWaitlistEntryToBook(
        this.waitlistEntry.id,
        this.selectedEvent.id,
        this.newDeadlineDate,
        this.newDeadlineTime,
        this.inviteNote,
        this.sendInviteToParentsAndGuardians,
        this.ageRestriction.codesFor(this.waitlistEntry.id),
        this.isResend()
      );
    } else {
      invitePromise = this.scheduledEventService.inviteWaitlistEntryToBook(
        this.waitlistEntry.id,
        undefined,
        this.newDeadlineDate,
        this.newDeadlineTime,
        this.inviteNote,
        this.sendInviteToParentsAndGuardians,
        this.ageRestriction.codesFor(this.waitlistEntry.id),
        this.isResend()
      );
    }

    invitePromise
      .then(() => {
        this.inviteSentCallback({
          text: (
            this.waitlistEntry.first_name + ' ' + this.waitlistEntry.last_name +
            (
              this.isResend()
                ? "'s invitation to book the event was sent again."
                : ' successfully invited to book the event.'
            )
          ),
          status: CallbackResultStatus.success
        });
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to send this invite.';
        } else if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingSubmit = false;
      });
  }

  private loadEventType() {
    this.loadingEventType = true;

    this.eventTypeService
      .getEventType(this.waitlistEntry.event_type_id)
      .then(eventType => { this.eventType = eventType; })
      .catch(reason => { this.serverError = reason.status; })
      .finally(() => { this.loadingEventType = false; });
  }

  private loadWaitlistEvent() {
    this.loadingWaitlistEvent = true;

    this.eventService
      .getEvent(this.waitlistScheduledEvent.event_id)
      .then(event => { this.waitlistEvent = event; })
      .catch(reason => { this.serverError = reason.status; })
      .finally(() => { this.loadingWaitlistEvent = false; });
  }

  /**
   * Whether a linked parent or guardian could receive this invitation. Only
   * needed when the entry has no address of its own, since ticking the
   * guardians box can then make the no-recipient warning wrong.
   */
  private loadGuardianEmailAvailability() {
    this.guardianWithEmailExists = null;

    if (!this.waitlistEntry.participant_customer_id) { return; }
    if (this.participantIsRemoved()) { return; }
    if (this.emailRecipients().length > 0) { return; }

    this.loadingRelations = true;

    // Increment the request count and save a local copy.
    this.relationsRequestCount++;
    const thisRequestCount = this.relationsRequestCount;

    this.customerService
      .getCustomerRelations(this.waitlistEntry.participant_customer_id)
      .then(result => {
        if (thisRequestCount !== this.relationsRequestCount) { return; }
        this.guardianWithEmailExists = result.results.some(isGuardianWithEmail);
      })
      .catch(() => {
        // Unknown: the warning stays silent rather than claim the unchecked.
        if (thisRequestCount !== this.relationsRequestCount) { return; }
        this.guardianWithEmailExists = null;
      })
      .finally(() => {
        if (thisRequestCount === this.relationsRequestCount) {
          this.loadingRelations = false;
        }
      });
  }
}
